import React, { type Dispatch, type SetStateAction } from "react";

import { useSelector, useDispatch } from "react-redux";
import Tooltip from "@material-ui/core/Tooltip";

import type { DepopIntegration } from "src/interfaces/plaidIntegration.interface";
import { type DataType } from "src/pages/GetStarted/ImportDialog";
import type MonthYear from "src/interfaces/monthYear.interface";
import { isDepopAllowedToDirectImportSelector } from "src/store/uploads/selector";
import {
  depopIntegrationDelete,
  depopIntegrationSyncChange,
} from "src/apiService/modules/depop";
import {
  depopSyncWithCheck,
  depopSyncInventory,
  depopSyncSales,
} from "src/store/integrationSync/actions/depop";
import { getDepopAbandonedResumeStatus } from "src/store/integrationSync/actions/depop/depopResumeOffer";
import { DirectImportSyncLockTtlMs } from "src/store/integrationSync/actions/syncLock";
import { bankAccountByIntegrationIdSelector } from "src/store/bankAccount/selector";

import PlatformIntegrationsTableRow from "../PlatformIntegrationsTableRow";
import useIsRunning from "../useIsRunning";
import type { DialogState } from "../types";

export function usePreventImportDepop(dataType: DataType) {
  return !useSelector((state: any) =>
    isDepopAllowedToDirectImportSelector(state, dataType)
  );
}

export const DepopNotice =
  "The Depop integration is still in beta testing. If you don’t wish to use this feature, you can delete the integration by clicking the red trash can icon on the ‘Depop’ line.";
export const DepopNoticeNode = (
  <Tooltip title={DepopNotice}>
    <span>Depop*</span>
  </Tooltip>
);

function DepopIntegrationTableRow({
  integration,
  setDialog,
  hideInventory,
}: {
  integration: DepopIntegration;
  setDialog: Dispatch<SetStateAction<DialogState>>;
  hideInventory: boolean;
}) {
  const dispatch = useDispatch();
  const isRunning = useIsRunning(
    integration.salesIsRunning,
    integration.inventoryIsRunning,
    DirectImportSyncLockTtlMs
  );
  const bankAccount = useSelector((state) =>
    integration?.id
      ? bankAccountByIntegrationIdSelector(state, integration.id)
      : undefined
  );

  const importProps = {
    integrationId: integration.id,
    fetchListings: (integrationId: string) =>
      dispatch(depopSyncInventory(integrationId)),
    fetchTransactions: async (
      integrationId: string,
      start: MonthYear,
      end: MonthYear
    ) => dispatch(depopSyncSales(integrationId, { start, end })),
    providerName: "Depop",
    usePreventImport: usePreventImportDepop,
  };

  return (
    <PlatformIntegrationsTableRow
      key={integration.id}
      name={bankAccount?.name || "Depop"}
      platform={DepopNoticeNode}
      isRunning={isRunning}
      sync={integration.sync}
      inventory={integration.sync && integration.inventory !== false}
      onPullDataNow={async () => {
        await dispatch(depopSyncWithCheck(integration.id, true));
      }}
      onImport={() => {
        const offer = getDepopAbandonedResumeStatus(integration);
        if (offer) {
          setDialog({
            type: "resume-or-import",
            props: {
              offer,
              platform: "depop",
              importProps,
            },
          });
          return;
        }
        setDialog({
          type: "import",
          props: importProps,
        });
      }}
      onSyncChanged={async (sync, inventory) => {
        await depopIntegrationSyncChange(integration.id, sync, inventory);
        if (sync) await dispatch(depopSyncWithCheck(integration.id, true));
      }}
      hideInventory={hideInventory}
      onDelete={() =>
        setDialog({
          type: "delete",
          props: {
            integration,
            deleteIntegration: depopIntegrationDelete,
          },
        })
      }
    />
  );
}

export default DepopIntegrationTableRow;
